import { useEffect, useMemo, useState } from 'react';

import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { CardDemonstrationBlur } from 'src/components/CardDemonstration';
import SubHeader from 'src/components/UI/SubHeader/SubHeader';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  FidcComparatorOption,
  FidcComparatorTableItem,
  getFidcComparator,
} from 'src/services/fidcComparator';
import { getPayWallBanner } from 'src/services/informative';

import FidcComparisonCharts from './FidcComparisonCharts';
import FidcComparisonSelector from './FidcComparisonSelector';
import FidcComparisonTable from './FidcComparisonTable';
import FidcSelector from './FidcSelector';
import { MAX_SELECTED_FIDCS, MIN_SELECTED_FIDCS } from './constants';
import { isValidBaseMonth, resolveBaseMonth } from './dateUtils';
import {
  orderFidcComparisonDataBySelection,
  parseInitialItems,
  reconcileSelectedFidcsWithTableData,
} from './selectionUtils';
const PAYWALL_PREVIEW_SELECTED_FIDCS: FidcComparatorOption[] = [
  {
    value: '101:Senior:1',
    operationId: 101,
    name: 'FIDC Exemplo A',
    cnpj: '00.000.000/0001-01',
    cota: 'Senior',
    serie: 1,
    label: 'FIDC Exemplo A | Senior | Série 1',
  },
  {
    value: '202:Mezanino:2',
    operationId: 202,
    name: 'FIDC Exemplo B',
    cnpj: '00.000.000/0001-02',
    cota: 'Mezanino',
    serie: 2,
    label: 'FIDC Exemplo B | Mezanino | Série 2',
  },
];
const PAYWALL_PREVIEW_TABLE_DATA: FidcComparatorTableItem[] = [
  {
    operationId: 101,
    name: 'FIDC Exemplo A',
    cnpj: '00.000.000/0001-01',
    adm: 'Administrador Exemplo',
    gest: 'Gestor Exemplo',
    cust: 'Custodiante Exemplo',
    ativoLastro: 'Duplicatas',
    condominio: 'Fechado',
    pl: 125000000,
    pdd: 1250000,
    cvnp: 640000,
    dc: 90000000,
    dataRef: '2025-01-31',
  },
  {
    operationId: 202,
    name: 'FIDC Exemplo B',
    cnpj: '00.000.000/0001-02',
    adm: 'Administrador Exemplo',
    gest: 'Gestor Exemplo',
    cust: 'Custodiante Exemplo',
    ativoLastro: 'Recebíveis',
    condominio: 'Aberto',
    pl: 98000000,
    pdd: 980000,
    cvnp: 420000,
    dc: 63700000,
    dataRef: '2025-01-31',
  },
];

export default function OperationFIDCComparison() {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const { auth } = useAuthContext();

  const hasPermission =
    !!auth?.user?.subscription?.plan?.permissions?.comparar_fidc;

  const { data: contentPayWallBanner } = useQuery(
    ['paywall', 'compareFidc'],
    () => getPayWallBanner('compareFidc'),
    { enabled: !hasPermission && !auth?.isLoading },
  );

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const initialBaseMonth = params.get('baseMonth');

  const [selectedFidcs, setSelectedFidcs] = useState<FidcComparatorOption[]>(
    parseInitialItems(params.get('items') ?? ''),
  );
  const [baseMonth, setBaseMonth] = useState<string>(
    resolveBaseMonth(initialBaseMonth),
  );

  const selectedValues = selectedFidcs.map((item) => item.value);
  const selectedValuesKey = selectedValues.join(',');
  const selectedOperationIds = selectedFidcs.map((item) => item.operationId);
  const selectedOperationIdsKey = selectedOperationIds.map(String).join(',');
  const isValid =
    selectedValues.length >= MIN_SELECTED_FIDCS &&
    selectedValues.length <= MAX_SELECTED_FIDCS;
  const displayedSelectedCount = hasPermission
    ? selectedFidcs.length
    : PAYWALL_PREVIEW_SELECTED_FIDCS.length;
  const hasValidBaseMonth = isValidBaseMonth(baseMonth);

  const handleBaseMonthChange = (nextBaseMonth: string) => {
    if (isValidBaseMonth(nextBaseMonth)) {
      setBaseMonth(nextBaseMonth);
    }
  };

  const { data, isLoading, error } = useQuery(
    ['fidc-comparator', selectedOperationIdsKey, baseMonth],
    () =>
      getFidcComparator({
        items: selectedOperationIds,
        baseMonth,
      }),
    {
      enabled:
        hasPermission && !auth?.isLoading && isValid && hasValidBaseMonth,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const hasError = Boolean(error);
  const orderedTableData = useMemo(
    () => orderFidcComparisonDataBySelection(selectedFidcs, data ?? []),
    [selectedFidcs, data],
  );

  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);

    if (selectedValuesKey) {
      nextParams.set('items', selectedValuesKey);
    } else {
      nextParams.delete('items');
    }

    nextParams.set('baseMonth', baseMonth);
    nextParams.delete('period');
    nextParams.delete('graphPeriod');

    const nextSearch = nextParams.toString();
    if (nextSearch !== location.search.replace(/^\?/, '')) {
      history.replace({ search: nextSearch });
    }
  }, [selectedValuesKey, baseMonth]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!data) return;

    setSelectedFidcs((currentSelectedFidcs) =>
      reconcileSelectedFidcsWithTableData(currentSelectedFidcs, data),
    );
  }, [data]);

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 3 },
        bgcolor: 'background.default',
      }}>
      <Stack gap={2.5}>
        <SubHeader
          title="Comparador de Fundos"
          titleIcon={<CompareArrowsIcon />}
          subTitle="Compare PL, PDD, atraso e direitos creditórios por FIDC, mantendo o mesmo período de competência para toda a análise."
          breadcrumbs1={{ title: 'Operações', link: '/operacoes' }}
          breadcrumbs2="Comparador FIDC"
        />

        <Stack direction="row" gap={1} flexWrap="wrap">
          <Chip
            variant="outlined"
            size="small"
            label={`${displayedSelectedCount}/${MAX_SELECTED_FIDCS} selecionados`}
          />
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
          }}>
          <Stack gap={3}>
            <Stack gap={2}>
              {hasPermission ? (
                <FidcSelector
                  values={selectedFidcs}
                  onChange={setSelectedFidcs}
                  isComparatorLoading={isLoading}
                />
              ) : (
                <Stack gap={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Prévia ilustrativa do comparador com dados fixos. Faça o
                    upgrade para selecionar FIDCs e carregar dados reais.
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {PAYWALL_PREVIEW_SELECTED_FIDCS.map((item) => (
                      <Chip
                        key={item.value}
                        label={item.label}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>

            <CardDemonstrationBlur
              allow={hasPermission}
              title={
                contentPayWallBanner?.compareFidc?.title ??
                'Recurso disponível em planos superiores'
              }
              subTitle={
                contentPayWallBanner?.compareFidc?.subTitle ??
                'Faça um upgrade para acessar o Comparador de FIDC.'
              }
              labelButton={
                contentPayWallBanner?.compareFidc?.labelButton ??
                'Fazer upgrade'
              }
              url={contentPayWallBanner?.compareFidc?.url ?? '/planos'}>
              <>
                {hasPermission && !isValid && (
                  <Alert severity="info" variant="outlined">
                    Selecione de {MIN_SELECTED_FIDCS} a {MAX_SELECTED_FIDCS}{' '}
                    FIDCs por classe/série para iniciar a comparação.
                  </Alert>
                )}

                {hasPermission && isValid && hasError && (
                  <Alert severity="error" variant="outlined">
                    Erro ao carregar dados de comparação. Tente novamente.
                  </Alert>
                )}

                {!hasPermission && (
                  <Box sx={{ overflowX: 'auto' }}>
                    <FidcComparisonTable
                      data={PAYWALL_PREVIEW_TABLE_DATA}
                      loading={false}
                    />
                  </Box>
                )}

                {hasPermission && isValid && !hasError && (
                  <>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', md: 'flex-start' }}
                      gap={2}>
                      <Stack gap={1} flex={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Visão comparativa
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Veja os dados do período de competência na tabela
                          comparativa.
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: { xs: 'flex-start', md: 'flex-end' },
                          minWidth: { md: 190 },
                        }}>
                        <FidcComparisonSelector
                          operationIds={selectedOperationIds}
                          baseMonth={baseMonth}
                          onBaseMonthChange={handleBaseMonthChange}
                        />
                      </Box>
                    </Stack>

                    <Box sx={{ overflowX: 'auto' }}>
                      <FidcComparisonTable
                        data={orderedTableData}
                        loading={isLoading}
                      />
                    </Box>

                    {!isLoading && data?.length === 0 && (
                      <Alert severity="info" variant="outlined">
                        Nenhum dado encontrado para os itens selecionados.
                      </Alert>
                    )}

                    {!isLoading && data && (
                      <FidcComparisonCharts
                        selectedFunds={selectedFidcs}
                        baseMonth={baseMonth}
                      />
                    )}
                  </>
                )}
              </>
            </CardDemonstrationBlur>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
